import { Component, OnInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  NonNullableFormBuilder,
  Validators,
} from '@angular/forms';
import { MatStepper } from '@angular/material/stepper';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AccountDetails,
  Country,
  SelfregisterApiService,
} from '@icullen/api/public';

import { Observable } from 'rxjs';
import { first, map } from 'rxjs/operators';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { AnalyticsService, AuthService } from '@icullen/api/shared';
import CustomValidation from './custom.validator';

type PersonalDetailsFormModel = FormGroup<{
  firstNameCtrl: FormControl<string>;
  lastNameCtrl: FormControl<string>;
  phoneNumberCtrl: FormControl<string>;
  countryCtrl: FormControl<string>;
  organisationNameCtrl: FormControl<string>;
  organisationTypeCtrl: FormControl<string>;
  jobTitleCtrl: FormControl<string>;
}>;

type PasswordFormModel = FormGroup<{
  passwordCtrl: FormControl<string>;
  confirmPasswordCtrl: FormControl<string>;
  privacyCtrl: FormControl<string>;
  newsletterOptInCtrl: FormControl<boolean>;
}>;

type EmailFormModel = FormGroup<{
  emailCtrl: FormControl<string>;
}>;
@Component({
  selector: 'public-self-register-form',
  templateUrl: './self-register-form.component.html',
  styleUrls: ['./self-register-form.component.scss'],
})
export class SelfRegisterFormComponent implements OnInit {
  //@ViewChild('stepper') private myStepper: MatStepper;

  emailFormGroup: EmailFormModel;
  personalDetailsFormGroup: PersonalDetailsFormModel;
  passwordFormGroup: PasswordFormModel;
  countries$: Observable<Country[]>;
  finalFormsubmitted = false;
  emailAlreadyExists = false;
  accountSuccess = true;
  excludedCountries = ['AA', 'EP', 'E1', 'AP', 'B1', 'B2'];

  constructor(
    private formBuilder: NonNullableFormBuilder,
    private route: ActivatedRoute,
    private recaptchaV3Service: ReCaptchaV3Service,
    private analyticsService: AnalyticsService,
    private authService: AuthService,
    private router: Router,
    private selfRegisterUserService: SelfregisterApiService
  ) {
    this.emailFormGroup = this.formBuilder.group({
      emailCtrl: ['', [Validators.required, Validators.email]],
    });
    this.personalDetailsFormGroup = this.formBuilder.group({
      firstNameCtrl: ['', Validators.required],
      lastNameCtrl: ['', Validators.required],
      phoneNumberCtrl: [''],
      countryCtrl: ['', Validators.required],
      organisationNameCtrl: ['', Validators.required],
      organisationTypeCtrl: ['', Validators.required],
      jobTitleCtrl: ['', Validators.required],
    });
    this.passwordFormGroup = this.formBuilder.group(
      {
        passwordCtrl: ['', [Validators.required, Validators.minLength(8)]],
        confirmPasswordCtrl: ['', [Validators.required]],
        privacyCtrl: ['', Validators.requiredTrue],
        newsletterOptInCtrl: [false],
      },
      {
        validators: [
          CustomValidation.match('passwordCtrl', 'confirmPasswordCtrl'),
        ],
      }
    );
  }

  ngOnInit(): void {
    const location = this.router.url;
    const user = this.authService.getCurrentUser();
    if (user) {
      console.log(location);
      if (user.UserType !== 'PROSPECT') {
        const destinationUrl =
          this.route.snapshot.queryParamMap.get('destination');
        if (destinationUrl) {
          window.location.href = destinationUrl;
        }
      } else {
        this.authService.logout(true);
        this.router.navigateByUrl(location);
        //window.location.href = location;
      }
    }
    this.countries$ = this.selfRegisterUserService.getCountries().pipe(
      map((data) => data.countries),
      map((countries) =>
        countries.filter(
          (country) => !this.excludedCountries.includes(country.code)
        )
      )
    );

    this.analyticsService.pushSelfRegisterEvent_StepEmail();
  }

  get fPass() {
    return this.passwordFormGroup.controls;
  }

  get fEmail() {
    return this.emailFormGroup.controls;
  }

  get fPersonal() {
    return this.personalDetailsFormGroup.controls;
  }

  validateEmailAndForwardToPersonalDetails(stepper: MatStepper) {
    console.log('Validation email');
    if (this.emailFormGroup.invalid) {
      return;
    } else {
      const email = this.fEmail.emailCtrl.value;
      this.selfRegisterUserService
        .validateEmail(email)
        .pipe(first())
        .subscribe((result) => {
          if (result.succeeded) {
            this.emailAlreadyExists = false;
            this.analyticsService.pushSelfRegisterEvent_StepPersonalDetails();
            stepper.next();
          } else {
            this.emailAlreadyExists = true;
            return;
          }
        });
      //stepper.next();
    }
  }

  nextToPasswordButton(stepper: MatStepper) {
    //const userDetails = this.getAccountDetails('');
    this.analyticsService.pushSelfRegisterEvent_StepPassword();
    stepper.next();
  }

  registerUser(stepper: MatStepper) {
    this.finalFormsubmitted = true;
    if (this.passwordFormGroup.invalid) {
      return;
    } else {
      this.analyticsService.pushSelfRegisterEvent_StepValidation();
      this.recaptchaV3Service.execute('registerUser').subscribe(
        (token) => {
          const userDetails = this.getAccountDetails(token);
          this.selfRegisterUserService
            .registerAccount(userDetails)
            .pipe(first())
            .subscribe((result) => {
              this.accountSuccess = result.succeeded;
              stepper.next();
            });
        },
        (error) => {
          this.accountSuccess = false;
          console.log('REcaptcha v3 error' + error);
          stepper.next();
        }
      );
    }
  }

  getAccountDetails(token: string) {
    const accountDetails = new AccountDetails();
    accountDetails.emailAddress = this.fEmail.emailCtrl.value;
    accountDetails.firstName = this.fPersonal.firstNameCtrl.value;
    accountDetails.lastName = this.fPersonal.lastNameCtrl.value;
    accountDetails.countryCode = this.fPersonal.countryCtrl.value;
    accountDetails.jobTitle = this.fPersonal.jobTitleCtrl.value;
    accountDetails.phoneNumber = this.fPersonal.phoneNumberCtrl.value;
    accountDetails.organizationName = this.fPersonal.organisationNameCtrl.value;
    accountDetails.organizationType = this.fPersonal.organisationTypeCtrl.value;
    accountDetails.password = this.fPass.passwordCtrl.value;
    accountDetails.destinationUrl =
      this.route.snapshot.queryParamMap.get('destination');
    accountDetails.recaptchaToken = token;
    accountDetails.newsletterOptIn = this.fPass.newsletterOptInCtrl.value;
    return accountDetails;
  }

  login() {
    const destinationUrl = this.route.snapshot.queryParamMap.get('destination');
    if (destinationUrl) {
      window.location.href = destinationUrl;
    } else {
      this.authService.loginSso('');
    }
  }

  backToPersonalDetails(stepper: MatStepper) {
    this.finalFormsubmitted = false;
    stepper.previous();
  }

  getResetPasswordUrl() {
    return this.authService.getResetPasswordUrl();
  }
}
